import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithAuth } from '../base-query';

/**
 * Staff-only credit-usage reporting — the same per-org / per-user consumption
 * digest the backend posts to Slack once a day, surfaced live for the
 * management portal. Server gates it to staff (403 unless the Clerk email ends
 * with @cleanlist.ai). Read-only — never touches billing.
 *
 * Types mirror the DTOs in cleanse_api/api/platform/admin.py
 * (DailyCreditUsageResponse → CreditUsageOrgRow → CreditUsageUserRow), which are
 * shaped from `build_daily_usage_report` (credit_ledger_daily_summary.py).
 */

/** Mirrors CreditUsageUserRow — one member's net consumption within an org. */
export interface CreditUsageUser {
  user_id: string | null;
  email: string | null;
  credits_used: number;
  ops: number;
}

/** Mirrors CreditUsageOrgRow — one org's net consumption, broken down by
 *  member. `users` is capped to the top spenders; `hidden_users` counts the
 *  tail folded into a "+N more" line (Slack-digest parity). */
export interface CreditUsageOrg {
  organization_id: string;
  organization_name: string | null;
  credits_used: number;
  ops: number;
  users: CreditUsageUser[];
  hidden_users: number;
}

/** Mirrors DailyCreditUsageResponse — totals + the top orgs (each with its top
 *  members) over the trailing `since_hours` window. `generated_at` is ISO-8601
 *  naive UTC; the window is [generated_at - since_hours, generated_at]. */
export interface DailyCreditUsageReport {
  since_hours: number;
  generated_at: string;
  total_credits: number;
  total_orgs: number;
  total_users: number;
  orgs: CreditUsageOrg[];
  hidden_orgs: number;
  /** Same-length window just before this one. Absent on servers before
   *  cleanse-api #2092. */
  previous_total_credits?: number;
  /** Net credits per hour (windows up to 48h) or per day, oldest first. */
  series?: { start: string; credits: number }[];
}

/** The window the Overview tile and the tab both open on, so they share one
 *  cached query. */
export const DEFAULT_CREDIT_WINDOW_HOURS = 24;

export const adminCreditUsageApi = createApi({
  reducerPath: 'adminCreditUsageApi',
  baseQuery: baseQueryWithAuth,
  endpoints: (builder) => ({
    // `hours` defaults to 24 (the digest window) on the server; the UI passes
    // 24, 168 (7d) or 720 (30d). `organizationId` narrows the report to one
    // org with every member listed.
    getDailyCreditUsage: builder.query<
      DailyCreditUsageReport,
      { hours?: number; organizationId?: string } | void
    >({
      query: (arg) => ({
        url: '/management/credit-usage/daily',
        params: {
          ...(arg?.hours ? { hours: arg.hours } : {}),
          ...(arg?.organizationId
            ? { organization_id: arg.organizationId }
            : {}),
        },
      }),
    }),
  }),
});

export const { useGetDailyCreditUsageQuery } = adminCreditUsageApi;
